import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Handsontable from "handsontable";
import { NestedHeaderSettings } from "handsontable/plugins/nestedHeaders";
import { CellChange } from "handsontable/settings";
import { ColumnSettings, GridSettings } from "handsontable/settings";

import { environment } from "../../../../core/environments/environment";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language } from "../../../constants/enums";
import { ColumnsIndices, DataEntry, DataEntryDetails } from "../../../models";
import { DataentryService } from "../../../services";

@Component({
  selector: "adaa-master-forecasted-values",
  imports: [],
  templateUrl: "./master-forecasted-values.html",
  styleUrl: "./master-forecasted-values.scss",
})
export class MasterForecastedValuesComponent implements AfterViewInit {
  private readonly _translateService = inject(TranslateService);
  private readonly _dataEntryService = inject(DataentryService);

  forecastedValuesDetails = input.required<DataEntryDetails>();
  valueChanged = output<DataEntryDetails>();

  hotTableContainer = viewChild.required<ElementRef>("hotTableContainer");

  isFormula = computed<boolean>(() => this.forecastedValuesDetails().formula !== Constants.FORMULA_STRING.NOFORMULA);
  metricsLength = computed<number>(() =>
    this.forecastedValuesDetails().metricDetails ? this.forecastedValuesDetails().metricDetails!.length : 0
  );
  private readonly _forecastedValueToSave = signal<DataEntryDetails>({ kpiId: 0 });
  private readonly _columnsIndices: ColumnsIndices = {
    optionsColumns: [],
    targetsColumns: [],
    actualColumns: [],
    auditAnnualActualColumns: [],
    calcColumns: [],
  };

  hotInstance: Handsontable;

  public ngAfterViewInit(): void {
    this._forecastedValueToSave.set(this.forecastedValuesDetails());

    const container = this.hotTableContainer().nativeElement;
    const options = this._getOptions();
    this.hotInstance = new Handsontable(container, options);
  }

  private _getOptions(): GridSettings {
    const currentLang = AdaaHelper.getCurrentLang();
    const { columnWidths, columns } = this._getColumns();

    return {
      data: this._getData(),
      rowHeaders: this._getRowHeaders(),
      rowHeaderWidth: 400,
      rowHeights: Constants.GRID_ROW_HEIGHT.DEFAULT,
      nestedHeaders: this._getNestedHeaders(),
      columns: columns,
      colWidths: columnWidths,
      afterChange: (changes, _source) => this._handleCellValueChanged(changes),
      //Same Conf
      stretchH: "none",
      height: "auto",
      viewportColumnRenderingOffset: 999999,
      viewportRowRenderingOffset: 999999,
      hiddenColumns: true,
      layoutDirection: currentLang === Language.Arabic ? "rtl" : "ltr",
      language: currentLang === Language.Arabic ? "ar-AR" : "en-US",
      licenseKey: environment.handsontable_key,
      themeName: "ht-theme-classic",
      afterGetColHeader(col, TH) {
        if (TH) {
          TH.id = `col-header-${col}`;
        }
      },
    };
  }

  private _getColumns() {
    const columnWidths: number[] = [];
    const columns: ColumnSettings[] = [];
    let index = 0;

    this.forecastedValuesDetails().govKPIValues?.forEach((e) => {
      if (this.isFormula()) {
        this.forecastedValuesDetails().metricDetails?.forEach((metric) => {
          columnWidths.push(200);
          columns.push({});

          this._columnsIndices.actualColumns.push({
            index: index,
            periodId: e.periodId,
            metricNameEN: metric.metricNameEN,
          });
          index++;
        });
      }

      columnWidths.push(200);
      columns.push({
        readOnly: this.isFormula() ? true : false,
        className: this.isFormula() ? "disabledCell" : undefined,
      });

      if (this.isFormula()) {
        this._columnsIndices.calcColumns.push({
          index: index,
          periodId: e.periodId,
        });
      } else {
        this._columnsIndices.actualColumns.push({
          index: index,
          periodId: e.periodId,
        });
      }
      index++;
    });

    return { columnWidths, columns };
  }

  private _getRowHeaders(): string[] {
    const headers: string[] = [];

    //GOV KPI
    headers.push(this._translateService.instant("kpi.govKpi"));

    //Entities
    this.forecastedValuesDetails().entityValues?.forEach((e) => {
      headers.push(AdaaHelper.getItemValueByToken(e, "entityName"));
    });

    return headers;
  }

  private _getNestedHeaders() {
    const isFormula = this.isFormula();
    const firstHeader: Array<string | NestedHeaderSettings> = [];
    const seconedHeader: Array<string | NestedHeaderSettings> = [];
    const thirdHeader: Array<string | NestedHeaderSettings> = [];

    this.forecastedValuesDetails().govKPIValues?.forEach((forecastedValue) => {
      firstHeader.push({
        label: this._dataEntryService.getLabelForPeriodColumn(
          forecastedValue,
          this.forecastedValuesDetails().frequency!
        ),
        colspan: isFormula ? this.metricsLength() + 1 : 1,
      });

      seconedHeader.push({
        label: this._translateService.instant("forecasted_values.title_single"),
        colspan: isFormula ? this.metricsLength() : 1,
      });

      if (isFormula) {
        seconedHeader.push({
          label: this._translateService.instant("data_entry.calc"),
          colspan: 1,
        });

        this.forecastedValuesDetails().metricDetails?.forEach((metric) => {
          thirdHeader.push(AdaaHelper.getItemValueByToken(metric, "metricName"));
        });

        thirdHeader.push("");
      }
    });

    return isFormula ? [firstHeader, seconedHeader, thirdHeader] : [firstHeader, seconedHeader];
  }

  private _getData() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let innerData: any[] = [];

    if (!this.isFormula()) {
      this.forecastedValuesDetails().govKPIValues?.forEach((forecastedValue) => {
        innerData.push(forecastedValue.value);
      });
      data.push(innerData);

      this.forecastedValuesDetails().entityValues?.forEach((e) => {
        innerData = [];
        e.values?.forEach((forecastedValue) => innerData.push(forecastedValue.value));
        data.push(innerData);
      });
    } else {
      this.forecastedValuesDetails().govKPIValues?.forEach((forecastedValue) => {
        forecastedValue.metrics?.forEach((metric) => {
          innerData.push(metric.value);
        });

        //Calc
        innerData.push(
          this._dataEntryService.calcFormulaForecasted(
            forecastedValue.metrics,
            this.forecastedValuesDetails().metricDetails,
            this.forecastedValuesDetails().formula
          )
        );
      });
      data.push(innerData);

      this.forecastedValuesDetails().entityValues?.forEach((e) => {
        innerData = [];

        e.values?.forEach((forecastedValue) => {
          forecastedValue.metrics?.forEach((metric) => {
            innerData.push(metric.value);
          });

          //Calc
          innerData.push(
            this._dataEntryService.calcFormulaForecasted(
              forecastedValue.metrics,
              this.forecastedValuesDetails().metricDetails,
              this.forecastedValuesDetails().formula
            )
          );
        });

        data.push(innerData);
      });
    }

    return data;
  }

  private _handleCellValueChanged = (changes: CellChange[] | null) => {
    if (!changes || !this.hotInstance || this.hotInstance.isDestroyed) return;

    changes.forEach((change) => {
      let [row, col, prev, next] = change as [number, number, number | undefined, number | undefined];
      let invalidCell = false;

      row = +row;
      col = +col;

      //If the change was on the calc column skip
      if (this._columnsIndices.calcColumns.find((e) => e.index === col)) return;

      if (!AdaaHelper.isDefinedAndNotEmpty(prev)) prev = undefined;
      if (!AdaaHelper.isDefinedAndNotEmpty(next)) next = undefined;

      //Avoid saving changes on empty cells
      if (!AdaaHelper.isDefined(prev) && !AdaaHelper.isDefined(next)) return;

      if (next !== undefined && isNaN(next)) {
        this.hotInstance.setCellMeta(row, col, "valid", false);
        invalidCell = true;
      } else {
        this.hotInstance.removeCellMeta(row, col, "valid");
      }

      const colIndex = this._columnsIndices.actualColumns.find((e) => e.index === col);
      if (!colIndex) return;

      if (row === 0) {
        const forecastedValue = this.forecastedValuesDetails().govKPIValues?.find(
          (e) => e.periodId === colIndex.periodId
        );
        if (!forecastedValue) return;

        if (this.isFormula()) {
          const metric = forecastedValue.metrics?.find((e) => e.metricNameEN === colIndex.metricNameEN);
          if (!metric) return;

          metric.value = invalidCell ? next : Number(next);
          metric.invalid = invalidCell;
          this._updateValueToSave(metric, undefined);

          const value = this._dataEntryService.calcFormulaForecasted(
            forecastedValue.metrics,
            this.forecastedValuesDetails().metricDetails,
            this.forecastedValuesDetails().formula
          );

          const calcCellIndex = this._columnsIndices.calcColumns.find((e) => e.periodId === colIndex.periodId);
          if (calcCellIndex) this.hotInstance.setDataAtCell(row, calcCellIndex.index, value);
        } else {
          forecastedValue.value = invalidCell ? next : Number(next);
          forecastedValue.invalid = invalidCell;
          this._updateValueToSave(forecastedValue, undefined);
        }
      } else {
        const entity = this.forecastedValuesDetails().entityValues![row - 1];
        if (!entity) return;

        const forecastedValue = entity.values?.find((e) => e.periodId === colIndex.periodId);
        if (!forecastedValue) return;

        if (this.isFormula()) {
          const metric = forecastedValue.metrics?.find((e) => e.metricNameEN === colIndex.metricNameEN);
          if (!metric) return;

          metric.value = invalidCell ? next : Number(next);
          metric.invalid = invalidCell;
          this._updateValueToSave(metric, entity.entityId);

          const value = this._dataEntryService.calcFormulaForecasted(
            forecastedValue.metrics,
            this.forecastedValuesDetails().metricDetails,
            this.forecastedValuesDetails().formula
          );

          const calcCellIndex = this._columnsIndices.calcColumns.find((e) => e.periodId === colIndex.periodId);
          if (calcCellIndex) this.hotInstance.setDataAtCell(row, calcCellIndex.index, value);
        } else {
          forecastedValue.value = invalidCell ? next : Number(next);
          forecastedValue.invalid = invalidCell;
          this._updateValueToSave(forecastedValue, entity.entityId);
        }
      }
    });

    this.hotInstance.render();
  };

  private _updateValueToSave(forecastedValue: DataEntry, entityId?: number) {
    if (!this._forecastedValueToSave().values) this._forecastedValueToSave().values = [];

    const entry = this._forecastedValueToSave().values?.find(
      (e) =>
        e.periodId === forecastedValue.periodId &&
        e.metricNameEN === forecastedValue.metricNameEN &&
        e.entityId === entityId
    );
    if (entry) {
      entry.value = forecastedValue.value;
      entry.invalid = forecastedValue.invalid;
    } else {
      this._forecastedValueToSave().values?.push({
        ...forecastedValue,
        entityId: entityId,
      });
    }

    this.valueChanged.emit(this._forecastedValueToSave());
  }
}
